/* Client MIS — anonymized programme trends for client-facing use. */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  BarChart3, Download, Sparkles, RefreshCw, TrendingUp,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  BarChart, Bar, CartesianGrid,
} from 'recharts';

const LABELS = {
  jlr: 'JLR', aabe: 'AaBee Circle', xiaomi: 'Xiaomi Platinum',
  me: 'Mind Escapes', aurelion: 'Aurelion', godrej: 'Godrej',
  ggc: 'Goa Gymkhana Club', kvb: 'KVB', redberyl: 'RedBeryl',
};
const NAVY = '#1B2A4A';
const GOLD = '#B88C40';

function StatCard({ label, value, testId }) {
  return (
    <div className="bg-[#FAF6F0] border border-[#E5DFD3] rounded-xl px-4 py-3 flex-1" data-testid={testId}>
      <div className="text-2xl font-bold tabular-nums" style={{ color: NAVY }}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#8A7B5C] mt-0.5">{label}</div>
    </div>
  );
}

function TopList({ title, items, color = GOLD, testId }) {
  const mx = Math.max(...(items || []).map(i => i.count), 1);
  return (
    <div className="bg-white border border-[#e5e2dc] rounded-xl p-4" data-testid={testId}>
      <h3 className="text-[11px] font-bold uppercase tracking-wider mb-3" style={{ color: NAVY }}>{title}</h3>
      {(!items || !items.length) ? (
        <div className="text-[11px] text-[#9CA3AF]">No data in this window</div>
      ) : items.map(i => (
        <div key={i.label} className="flex items-center gap-2 mb-1.5">
          <div className="w-32 text-[11px] truncate" title={i.label}>{i.label}</div>
          <div className="flex-1 h-2.5 bg-[#EFEAE0] rounded">
            <div className="h-2.5 rounded" style={{ width: `${Math.max(3, (i.count / mx) * 100)}%`, background: color }} />
          </div>
          <div className="text-[10.5px] tabular-nums text-[#6B6B6B] w-10 text-right">{i.count.toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}

const TREND_BADGE = { rising: '▲ rising', new: '★ new', steady: '● steady' };

export default function ClientMisPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  const [themesBusy, setThemesBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = () => {
    setData(null);
    api.get(`/client-mis/${clientId}?months=${months}`).then(r => setData(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, months]);

  const generateThemes = async () => {
    setThemesBusy(true);
    try {
      await api.post(`/client-mis/${clientId}/themes/generate`, null, { timeout: 120000 });
      toast.success('Fresh anonymous themes distilled');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Theme generation failed');
    } finally { setThemesBusy(false); }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get(`/client-mis/${clientId}/report.pdf?months=${months}`,
        { responseType: 'blob', timeout: 90000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${clientId}_mis.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF export failed'); }
    finally { setPdfBusy(false); }
  };

  const s = data?.stats;
  return (
    <div className="p-8 max-w-6xl space-y-5" data-testid="client-mis-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: NAVY, fontFamily: 'Fraunces, serif' }}>
            <BarChart3 size={26} color={GOLD} /> Client MIS · {LABELS[clientId] || clientId}
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Client-facing trends — fully anonymised, no member names anywhere.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={clientId} onChange={(e) => navigate(`/client-mis/${e.target.value}`)}
            data-testid="client-mis-programme-select"
            className="text-[12px] border border-[#d8d4cc] rounded-md px-2 py-1.5 bg-white">
            {Object.entries(LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {[3, 6, 12].map(mo => (
            <button key={mo} onClick={() => setMonths(mo)} data-testid={`client-mis-window-${mo}`}
              className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border ${
                months === mo ? 'bg-[#1B2A4A] text-white border-[#1B2A4A]' : 'bg-white text-[#1B2A4A] border-[#d8d4cc]'}`}>
              {mo}m
            </button>
          ))}
          <button onClick={downloadPdf} disabled={pdfBusy} data-testid="client-mis-export-pdf"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold text-white disabled:opacity-50"
            style={{ background: GOLD }}>
            <Download size={13} /> {pdfBusy ? 'Building PDF…' : 'Branded PDF'}
          </button>
        </div>
      </div>

      {!data ? (
        <div className="text-sm text-[#9CA3AF]">Loading…</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-3">
            <StatCard label="Requests" value={s.total_requests.toLocaleString()} testId="mis-stat-requests" />
            <StatCard label="Closure rate" value={`${s.closure_rate}%`} testId="mis-stat-closure" />
            <StatCard label="Avg resolution" value={s.avg_resolution_hours ? `${Math.round(s.avg_resolution_hours)}h` : '—'} testId="mis-stat-resolution" />
            <StatCard label="Members served" value={s.members_served.toLocaleString()} testId="mis-stat-members" />
          </div>

          <div className="bg-white border border-[#e5e2dc] rounded-xl p-4" data-testid="mis-monthly-chart">
            <h3 className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: NAVY }}>
              Monthly request volume
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.monthly} margin={{ top: 6, right: 12, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="raised" stroke={NAVY} strokeWidth={2.5} dot={{ r: 3 }} name="Raised" />
                <Line type="monotone" dataKey="closed" stroke={GOLD} strokeWidth={2} dot={{ r: 3 }} name="Closed" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white border border-[#e5e2dc] rounded-xl p-4" data-testid="mis-themes">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: NAVY }}>
                <Sparkles size={13} color={GOLD} /> What members are asking for
              </h3>
              <button onClick={generateThemes} disabled={themesBusy} data-testid="mis-generate-themes"
                className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border border-[#d8d4cc] hover:bg-[#faf6f0] disabled:opacity-50"
                style={{ color: NAVY }}>
                <RefreshCw size={11} className={themesBusy ? 'animate-spin' : ''} />
                {themesBusy ? 'Distilling with Claude…' : (data.themes ? 'Refresh themes' : 'Distil themes')}
              </button>
            </div>
            {data.themes?.themes?.length ? (
              <div className="grid md:grid-cols-2 gap-2">
                {data.themes.themes.map((t, i) => (
                  <div key={i} className="border-l-[3px] rounded-r-lg px-3 py-2 bg-[#FAF6F0]" style={{ borderColor: GOLD }} data-testid={`mis-theme-${i}`}>
                    <div className="text-[12.5px] font-bold flex items-center gap-2" style={{ color: NAVY }}>
                      <TrendingUp size={12} color={GOLD} /> {t.theme}
                      <span className="text-[9.5px] font-semibold" style={{ color: GOLD }}>{TREND_BADGE[t.trend]}</span>
                    </div>
                    <div className="text-[11.5px] text-[#4A4A4A] mt-0.5">{t.detail}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11.5px] text-[#9CA3AF]">
                No themes distilled yet — click "Distil themes" and Claude will summarise the last 60 days of requests into anonymous demand trends.
              </p>
            )}
            {data.themes?.generated_at && (
              <p className="text-[9.5px] text-[#9CA3AF] mt-2">
                Distilled {new Date(data.themes.generated_at).toLocaleString('en-IN')} · anonymised
              </p>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <TopList title="Service categories" items={data.categories.slice(0, 8)} testId="mis-categories" />
            <TopList title="Request cities" items={data.cities.slice(0, 8)} color={NAVY} testId="mis-cities" />
            <TopList title="Sub-categories" items={data.sub_categories.slice(0, 8)} testId="mis-subcategories" />
            <TopList title="Channels" items={data.channels} color={NAVY} testId="mis-channels" />
          </div>

          <div className="bg-white border border-[#e5e2dc] rounded-xl p-4" data-testid="mis-benefit-chart">
            <h3 className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: NAVY }}>Benefit pillars</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.benefits} margin={{ top: 6, right: 12, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill={GOLD} radius={[4, 4, 0, 0]} name="Requests" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <p className="text-[11px] text-[#9CA3AF]">
            Everything on this page is aggregate-only. Excludes our own outreach nudges and soul artefacts — real member requests only.
          </p>
        </>
      )}
    </div>
  );
}
